/**
 * Fichiers JavaScript d'un écran chargés à la demande (import dynamique) qui ne se chargent pas : coupure réseau,
 * connexion refusée par le serveur, ou onglet ouvert avant une nouvelle version de l'application (les anciens
 * fichiers n'existent plus sur le serveur).
 * Le navigateur mémorise l'échec d'un fichier (Chrome : un nouvel import de la même adresse échoue aussitôt, sans
 * nouvelle requête) : la reprise réimporte donc le fichier à une adresse différente (?reprise=n), et si l'échec vient
 * d'un fichier dont il dépend, seul un rechargement de la page le corrige.
 * Fichier sans dépendance : utilisé par les routes (app.routes), la navigation (navigation-etat), la connexion et
 * les composants chargés à la demande (composantCollab de fonctionnalites).
 */

// #region Reconnaissance de l'erreur

/** Erreur de chargement d'un écran (import dynamique d'un fichier JavaScript en échec). */
export function estErreurChargementEcran(err: unknown): boolean {
  const message = err instanceof Error ? `${err.name} ${err.message}` : String(err ?? '');
  return /dynamically imported module|importing a module script failed|error loading dynamically|ChunkLoadError|Loading chunk/i.test(
    message,
  );
}

/**
 * Adresse du fichier en échec, lue dans le message de l'erreur (Chrome, Firefox ; Safari ne la donne pas), sans sa
 * requête (?reprise=n d'un essai précédent). Seulement un fichier JavaScript du site lui-même : jamais d'import d'une
 * autre origine.
 */
export function adresseEnEchec(err: unknown, origine: string): string | null {
  const message = err instanceof Error ? err.message : String(err ?? '');
  const trouve = /(https?:\/\/[^\s?#'"]+\.m?js)/.exec(message)?.[1];
  if (!trouve) {
    return null;
  }
  try {
    return new URL(trouve).origin === origine ? trouve : null;
  } catch {
    return null;
  }
}
// #endregion

// #region Reprise automatique

/** Attente avant chaque nouvel essai : une coupure brève (connexion refusée une fois) a le temps de passer. */
export const DELAIS_REPRISE: readonly number[] = [300, 800];

/** Import d'un fichier par son adresse complète (nouvel essai à une adresse que le navigateur n'a pas mémorisée). */
const importerAdresse = (adresse: string): Promise<unknown> => import(/* @vite-ignore */ adresse);

const attendre = (ms: number): Promise<void> => new Promise((fin) => setTimeout(fin, ms));

/**
 * Import d'un écran ou d'un composant chargé à la demande, avec deux nouveaux essais (après DELAIS_REPRISE) si le
 * fichier ne se charge pas ; toute autre erreur remonte aussitôt. À écrire autour de l'import lui-même, l'export
 * étant choisi après : `avecReprise(() => import('./x.component')).then((m) => m.XComponent)`.
 * Un nouvel essai réimporte l'adresse en échec suivie de ?reprise=n quand le message la donne (le navigateur ne
 * retente pas la même adresse) ; sinon il rappelle `charger`. `importer` n'est remplacé que par les tests.
 */
export async function avecReprise<M>(
  charger: () => Promise<M>,
  importer: (adresse: string) => Promise<unknown> = importerAdresse,
): Promise<M> {
  let erreur: unknown;
  for (let essai = 0; essai <= DELAIS_REPRISE.length; essai++) {
    try {
      if (essai === 0) {
        return await charger();
      }
      await attendre(DELAIS_REPRISE[essai - 1]);
      const adresse = adresseEnEchec(erreur, globalThis.location?.origin ?? '');
      return adresse ? ((await importer(`${adresse}?reprise=${essai}`)) as M) : await charger();
    } catch (err) {
      if (!estErreurChargementEcran(err)) {
        throw err;
      }
      erreur = err;
    }
  }
  throw erreur;
}
// #endregion

// #region Rechargement de la page

/** Clé de session : heure du dernier rechargement automatique (évite une boucle si l'échec persiste). */
const CLE_RECHARGEMENT = 'logaia.rechargementEcran';
/** Pas de second rechargement automatique dans cet intervalle : l'échec vient alors d'ailleurs (réseau). */
const INTERVALLE_RECHARGEMENT = 30_000;
/** Rechargement lancé : la page va être quittée (la connexion ne doit pas vider la session entre-temps). */
let rechargementLance = false;

/**
 * Écran ou composant toujours pas chargé après la reprise : la page est rechargée une fois, vers `adresse` (écran
 * demandé) ou sur place, ce qui corrige un fichier dépendant en échec ou une nouvelle version de l'application.
 * Renvoie false si un rechargement automatique vient d'avoir lieu (échec persistant : rien n'est fait, l'appelant
 * affiche l'échec).
 */
export function rechargerApresEchecChargement(adresse?: string): boolean {
  try {
    const dernier = Number(sessionStorage.getItem(CLE_RECHARGEMENT) ?? 0);
    if (Date.now() - dernier < INTERVALLE_RECHARGEMENT) {
      return false;
    }
    sessionStorage.setItem(CLE_RECHARGEMENT, String(Date.now()));
  } catch {
    // Stockage indisponible : on recharge quand même (une boucle reste bornée par le navigateur et l'utilisateur).
  }
  rechargementLance = true;
  // Après la fin de la navigation en échec : le routeur remet l'adresse en place à ce moment-là, ce qui annulerait un
  // rechargement lancé avant.
  setTimeout(() => (adresse ? window.location.assign(adresse) : window.location.reload()));
  return true;
}

/** Un rechargement automatique de la page est en cours (la page va être quittée). */
export function rechargementEnCours(): boolean {
  return rechargementLance;
}
// #endregion
